import { Divider, Spinner } from "@heroui/react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Flag, X } from "lucide-react";
import { useEffect, useState } from "react";

import { usePanel } from "@/contexts/PanelContext";

import { useAppContext } from "../../contexts/AppContext";
import { oapi } from "../../lib/utils";
import { Button } from "../core/Button";
import { UserCard } from "../Profile/UserCard";
import { SIDEBAR_SPRING } from "../SidebarBase";
import { ReportPixelModal } from "./ReportPixelModal";
import { SmallCanvas } from "./SmallCanvas";

interface IPixel {
  id: number;
  userId: string;
  x: number;
  y: number;
  color: string;
  createdAt: Date;
}

interface IUser {
  sub: string;
  username: string;
  display_name: string | null;
  picture_url: string | null;
  profile_url: string | null;
  isAdmin: boolean;
  isModerator: boolean;
}

interface IInstance {
  hostname: string;
  name: string | null;
  logo_url: string | null;
  banner_url: string | null;
}

const parsePixelResponse = (
  input: Omit<IPixel, "createdAt"> & { createdAt: string },
): IPixel => {
  const { createdAt, ...rest } = input;
  return {
    ...rest,
    createdAt: new Date(createdAt),
  };
};

interface WhoisTarget {
  x: number;
  y: number;
  surrounding: string[][];
  requestId: string;
}

const PixelWhoisPanel = ({
  target,
  reduceMotion,
  onClose,
}: {
  target: WhoisTarget;
  reduceMotion: boolean;
  onClose: () => void;
}) => {
  const { user } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [whois, setWhois] = useState<{
    pixel: IPixel;
    otherPixels: number;
    user: IUser | null;
    instance: IInstance | null;
  }>();
  const [report, setReport] = useState(false);

  useEffect(() => {
    let active = true;

    oapi
      .GET("/canvas/pixel/{x}/{y}", {
        params: {
          path: {
            x: target.x,
            y: target.y,
          },
        },
      })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (data) {
          setWhois({
            pixel: parsePixelResponse(data.pixel),
            otherPixels: data.otherPixels,
            user: data.user,
            instance: data.instance,
          });
        }
        if (error) console.error("Error while fetching whois", response);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [target.x, target.y]);

  return (
    <>
      <motion.div
        aria-hidden="true"
        className="min-w-[20rem] max-w-[75vw] md:max-w-[30vw] bg-white dark:bg-black fixed right-0 h-full shadow-xl overflow-y-auto z-50 top-0"
        initial={{ x: "150%" }}
        animate={{ x: "50%" }}
        exit={{ x: "150%" }}
        transition={reduceMotion ? { duration: 0 } : SIDEBAR_SPRING}
      />
      <motion.div
        animate={{ x: 0 }}
        aria-hidden={false}
        data-sidebar-side="Right"
        className="canvas-sidebar canvas-sidebar--right min-w-[20rem] max-w-[75vw] md:max-w-[30vw] bg-white dark:bg-black text-black dark:text-white flex flex-col fixed right-0 h-full shadow-xl overflow-y-auto z-50 top-0"
        exit={{ x: "100%" }}
        initial={{ x: "100%" }}
        transition={reduceMotion ? { duration: 0 } : SIDEBAR_SPRING}
      >
        {loading && (
          <div className="absolute top-0 left-0 w-full h-full bg-black/25 flex justify-center items-center z-50">
            <div className="flex flex-col bg-white p-5 rounded-lg gap-5">
              <Spinner />
              Loading
            </div>
          </div>
        )}
        <div className="sticky top-0 z-20 bg-white dark:bg-black">
          <header className="flex p-4 justify-between items-center">
            <h1 className="text-xl">Pixel Whois</h1>
            <Button className="ml-4" size="sm" isIconOnly onPress={onClose}>
              <X aria-hidden="true" size={18} />
            </Button>
          </header>
          <Divider />
        </div>
        <div className="w-full h-52 bg-gray-200 dark:bg-gray-800 flex justify-center items-center">
          <div className="w-[128px] h-[128px] bg-white">
            <SmallCanvas
              surrounding={target.surrounding}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <section className="flex flex-col gap-1 px-4 py-1">
          {whois?.user && <UserCard user={whois.user} />}
        </section>
        <section className="flex flex-col gap-1 px-4 py-1">
          <table className="w-full border-separate border-spacing-y-1 text-sm">
            <tbody>
              <tr>
                <th className="w-32 whitespace-nowrap pr-3 text-right align-top font-semibold">
                  Placed At
                </th>
                <td className="break-words text-left align-top">
                  {whois?.pixel.createdAt?.toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "medium",
                  })}
                </td>
              </tr>
              <tr>
                <th className="w-32 whitespace-nowrap pr-3 text-right align-top font-semibold">
                  Covered Pixels
                </th>
                <td className="break-words text-left align-top">
                  {whois?.otherPixels}
                </td>
              </tr>
            </tbody>
          </table>
          {user ? (
            <Button color="danger" onPress={() => setReport(true)}>
              <Flag aria-hidden="true" size={16} />
              Report Pixel
            </Button>
          ) : (
            <Button color="danger" isDisabled variant="bordered">
              <Flag aria-hidden="true" size={16} />
              Report Pixel (logged out)
            </Button>
          )}
        </section>

        <ReportPixelModal
          open={report}
          onOpen={setReport}
          pixel={whois?.pixel}
        />
      </motion.div>
    </>
  );
};

export const PixelWhoisSidebar = () => {
  const Panel = usePanel();
  const reduceMotion = useReducedMotion() ?? false;
  const target = Panel.state.whois;

  return (
    <AnimatePresence initial={false}>
      {target && (
        <PixelWhoisPanel
          key={target.requestId}
          target={target}
          reduceMotion={reduceMotion}
          onClose={() => Panel.dispatch(["whois", undefined])}
        />
      )}
    </AnimatePresence>
  );
};
